import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { toDraft } from './toDraft';
import { getSchedulingWindows, type SchedulingWindowRow, type SchedulingWindowsPage } from 'api/scheduling_windows/endpoints/getSchedulingWindows';
import { deleteSchedulingWindow as deleteSchedulingWindowRequest } from 'api/scheduling_windows/endpoints/deleteSchedulingWindow';
import type { FilterConfig, ListColumnConfig } from 'api/shared/listConfig';

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface LoadingState {
  fetchSchedulingWindows: boolean;
  deleteSchedulingWindow: boolean;
}

/**
 * Scheduling-window list state.
 *
 * `nodes` and `items` are the same array under two names, which several pages read
 * by one name and several by the other; collapsing them would churn five call sites
 * for no behavioural gain, so it is left for whoever next touches those pages.
 *
 * Both hold the **validated** read row: since the create/update thunks are gone —
 * the edit dialog saves through the Layer-1 endpoints and the list refetches — the
 * only writers are the boundary-validated fetch and a delete that filters. That is
 * what makes typing the array to `SchedulingWindowRow` honest rather than
 * aspirational (see the multi-writer caveat in #757).
 */
interface State {
  nodes: readonly SchedulingWindowRow[];
  items: readonly SchedulingWindowRow[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: ListColumnConfig[];
  filtersConfig: FilterConfig[];
  loading: LoadingState;
}

const DEFAULT_LIMIT = 2000;

const initialState: State = {
  nodes: [],
  items: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: DEFAULT_LIMIT,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchSchedulingWindows: false,
    deleteSchedulingWindow: false,
  },
};

/**
 * GET /scheduling_windows. The high default limit is deliberate: four of the six
 * callers are dropdowns that need every window, not a page of them.
 */
export const fetchSchedulingWindows = createAsyncThunk<SchedulingWindowsPage, Record<string, unknown> | undefined, { rejectValue: string }>(
  'schedulingWindows/fetch',
  async (params, { rejectWithValue }) => {
    try {
      return await getSchedulingWindows(
        flattenQuery({
          ...(params ?? {}),
          sortColumn: params?.sortColumn ?? 'created_at',
          sortOrder: String(params?.sortOrder ?? 'ASC').toUpperCase(),
          page: params?.page ?? 1,
          limit: params?.limit ?? DEFAULT_LIMIT,
        })
      );
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch scheduling windows'));
    }
  }
);

export const deleteSchedulingWindow = createAsyncThunk<
  { zone_name: string; window_name: string },
  { zone_name: string; window_name: string },
  { rejectValue: string }
>('schedulingWindows/delete', async ({ zone_name, window_name }, { rejectWithValue }) => {
  try {
    await deleteSchedulingWindowRequest(zone_name, window_name);
    return { zone_name, window_name };
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to delete scheduling window'));
  }
});

const schedulingWindowSlice = createSlice({
  name: 'schedulingWindow',
  initialState,
  reducers: {},
  extraReducers: (b) => {
    b.addCase(fetchSchedulingWindows.pending, (state) => {
      state.status = 'loading';
      state.error = null;
      state.loading.fetchSchedulingWindows = true;
    });
    b.addCase(fetchSchedulingWindows.fulfilled, (state, action) => {
      const { nodes, pagination, config, filtersConfig } = action.payload;

      state.nodes = toDraft(nodes);
      state.items = state.nodes;
      state.config = toDraft(config);
      state.filtersConfig = toDraft(filtersConfig);

      state.pagination.totalItems = pagination.total_records;
      state.pagination.totalPages = pagination.total_pages;
      state.pagination.pageSize = pagination.limit;
      // A deleted last row can leave the requested page past the end; fall back to
      // the first rather than showing an empty table.
      state.pagination.currentPage = pagination.current_page > pagination.total_pages && pagination.total_pages > 0 ? 1 : pagination.current_page;

      state.status = 'succeeded';
      state.loading.fetchSchedulingWindows = false;
    });
    b.addCase(fetchSchedulingWindows.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload ?? action.error.message ?? 'Failed to fetch scheduling windows';
      state.loading.fetchSchedulingWindows = false;
    });

    b.addCase(deleteSchedulingWindow.pending, (state) => {
      state.loading.deleteSchedulingWindow = true;
    });
    b.addCase(deleteSchedulingWindow.fulfilled, (state, action) => {
      state.loading.deleteSchedulingWindow = false;
      state.nodes = state.nodes.filter((row) => !(row.zone_name === action.payload.zone_name && row.name === action.payload.window_name));
      state.items = state.nodes;
    });
    b.addCase(deleteSchedulingWindow.rejected, (state, action) => {
      state.loading.deleteSchedulingWindow = false;
      state.status = 'failed';
      state.error = action.payload ?? action.error.message ?? 'Failed to delete scheduling window';
      // This toast is the ONLY feedback a failed delete produces: `deleteExecutor`
      // confirms, deletes and refreshes without inspecting the outcome, and
      // `store.dispatch` of a thunk never rejects. Same as locks and zones.
      const toastId = 'data-failed-to-delete-scheduling-window';
      if (!toast.isActive(toastId)) {
        toast.error(`Failed to delete the scheduling window! ${state.error}`, { toastId });
      }
    });
  },
});

export default schedulingWindowSlice.reducer;
